import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useLocation } from '../../../contexts/LocationContext';
import api from '../../../utils/apiConfig';

const Pipeline = () => {
  const { selectedLocation } = useLocation();
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [statsData, setStatsData] = useState({
    totalRevenue: 0,
    conversionRate: 0,
    totalLeads: 0,
  });

  // Fetch leads and stats for selected location
  useEffect(() => {
    const load = async () => {
      if (!selectedLocation?._id) return;
      try {
        setLoading(true);
        setError('');
        // Fetch leads with server-enforced limit (max 100) using simple pagination
        const limit = 100;
        let page = 1;
        let allLeads = [];
        // loop with a hard cap of 10 pages to avoid runaway
        // stops early when a page returns fewer than limit
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const resp = await api.get(`/marketing/leads?locationId=${selectedLocation._id}&page=${page}&limit=${limit}`);
          if (resp.data?.success) {
            const batch = resp.data.data.leads || [];
            allLeads = allLeads.concat(batch);
            if (batch.length < limit || page >= 10) break;
            page += 1;
          } else {
            break;
          }
        }
        setLeads(allLeads);
        const statsRes = await api.get(`/marketing/leads/stats/${selectedLocation._id}`);
        if (statsRes.data?.success) {
          const s = statsRes.data.data.stats;
          setStatsData({
            totalRevenue: Number(s.totalRevenue) || 0,
            conversionRate: Number(s.conversionRate) || 0,
            totalLeads: Number(s.totalLeads) || 0,
          });
        }
      } catch (e) {
        console.error('Pipeline load error', e);
        setError(e?.response?.data?.message || 'Failed to load pipeline');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedLocation]);

  // Map leads to pipeline stages
  const pipelineStages = useMemo(() => {
    const stages = [
      { key: 'LEAD', name: 'Lead', color: 'info', statuses: ['NEW', 'CONTACTED'] },
      { key: 'QUALIFIED', name: 'Qualified', color: 'primary', statuses: ['QUALIFIED'] },
      { key: 'PROPOSAL', name: 'Proposal', color: 'warning', statuses: ['PROPOSAL'] },
      { key: 'NEGOTIATION', name: 'Negotiation', color: 'secondary', statuses: ['NEGOTIATION'] },
      { key: 'CLOSED_WON', name: 'Closed Won', color: 'success', statuses: ['CLOSED_WON'] },
    ];

    const now = Date.now();
    const stageData = stages.map(stage => {
      const inStage = leads.filter(l => stage.statuses.includes(l.status));
      const deals = inStage.map(l => {
        const fullName = `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email || 'Lead';
        const details = (typeof l.details === 'object' && l.details) ? l.details : {};
        const interestBudget = Number(details?.interest?.budget) || 0;
        const wonRevenue = Number(details?.conversion?.revenue) || 0;
        const value = stage.key === 'CLOSED_WON' ? wonRevenue : interestBudget;
        const lastActivity = details?.lastActivity ? new Date(details.lastActivity).getTime() : null;
        const createdAt = l.createdAt ? new Date(l.createdAt).getTime() : now;
        const since = lastActivity || createdAt;
        const daysInStage = Math.max(0, Math.round((now - since) / (1000 * 60 * 60 * 24)));
        // Simple probability heuristic by status/score
        const score = Number(l.score) || 0;
        const baseProb = (
          stage.key === 'LEAD' ? 20 :
          stage.key === 'QUALIFIED' ? 45 :
          stage.key === 'PROPOSAL' ? 65 :
          stage.key === 'NEGOTIATION' ? 80 : 100
        );
        const probability = Math.min(100, Math.max(baseProb, Math.round(score)));
        return {
          id: l.id,
          customer: fullName,
          value,
          probability,
          daysInStage
        };
      });
      const totalValue = deals.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
      return {
        name: stage.name,
        color: stage.color,
        count: inStage.length,
        value: totalValue,
        deals
      };
    });
    return stageData;
  }, [leads]);

  const stats = useMemo(() => ([
    {
      title: 'Total Pipeline Value',
      value: `$${pipelineStages.reduce((acc, s) => acc + s.value, 0).toLocaleString()}`,
      icon: <MoneyIcon sx={{ fontSize: 40, color: 'success.main' }} />,
    },
    {
      title: 'Active Deals',
      value: pipelineStages.reduce((acc, s) => acc + s.count, 0).toString(),
      icon: <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Conversion Rate',
      value: `${statsData.conversionRate}%`,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main' }} />,
    },
    {
      title: 'Total Revenue (Won)',
      value: `$${Number(statsData.totalRevenue || 0).toLocaleString()}`,
      icon: <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
    },
  ]), [pipelineStages, statsData]);

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setDetailsOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Sales Pipeline
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          onClick={() => setAddDealOpen(true)}
          sx={{ fontWeight: 'bold' }}
        >
          Add Deal
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pipeline Visualization */}
      <Grid container spacing={2}>
        {pipelineStages.map((stage, index) => (
          <Grid item xs={12} md={2.4} key={index}>
            <Card sx={{ height: '100%', minHeight: 400 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {stage.name}
                  </Typography>
                  <Chip
                    label={stage.count}
                    color={stage.color}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  ${stage.value.toLocaleString()}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Deals in this stage */}
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {stage.deals.map((deal) => (
                    <Card 
                      key={deal.id} 
                      variant="outlined" 
                      sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      onClick={() => handleDealClick(deal)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }}>
                            {deal.customer.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {deal.customer}
                          </Typography>
                        </Box>
                        
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          ${deal.value.toLocaleString()}
                        </Typography>
                        
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Probability</Typography>
                            <Typography variant="caption">{deal.probability}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={deal.probability} 
                            color={deal.probability > 70 ? 'success' : deal.probability > 40 ? 'warning' : 'error'}
                          />
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary">
                          {deal.daysInStage} days in stage
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Deal Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deal Details</DialogTitle>
        <DialogContent>
          {selectedDeal && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedDeal.customer}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                ${selectedDeal.value.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Probability: {selectedDeal.probability}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Days in current stage: {selectedDeal.daysInStage}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit Deal</Button>
        </DialogActions>
      </Dialog>

      {/* Add Deal Dialog */}
      <Dialog open={addDealOpen} onClose={() => setAddDealOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Deal</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Customer Name"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Deal Value"
            type="number"
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Stage</InputLabel>
            <Select label="Stage">
              {pipelineStages.map((stage) => (
                <MenuItem key={stage.name} value={stage.name}>
                  {stage.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Probability (%)"
            type="number"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDealOpen(false)}>Cancel</Button>
          <Button variant="contained">Add Deal</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pipeline;
